import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Star } from 'lucide-react'
import { usePicks } from '@/hooks/usePicks'
import { TopPickCard } from '@/components/TopPickCard'
import { SecondaryPickCard } from '@/components/SecondaryPickCard'
import { PickDetailSheet } from '@/components/PickDetailSheet'
import { CategoryPerformanceCard } from '@/components/CategoryPerformanceCard'
import { SectionHeader } from '@/components/SectionHeader'
import { EmptyState } from '@/components/EmptyState'
import { SkeletonList } from '@/components/LoadingSkeleton'
import { ErrorState } from '@/components/ErrorState'
import { DateNav } from '@/components/DateNav'
import { UpgradePrompt } from '@/components/UpgradePrompt'
import { ResponsibleNotice } from '@/components/ResponsibleNotice'
import { RunBadge } from '@/components/RunBadge'
import { PillTabs } from '@/components/PillTabs'
import { todayISO } from '@/lib/date'
import type { PickLeg } from '@/types/miniApp'

type PicksTab = 'top_ia' | 'safest' | 'secondary'

export function PicksPage() {
  const [date, setDate] = useState(todayISO())
  const isToday = date === todayISO()
  // Fase R.8: `?run=run1|run2` -- llegada desde los bloques de franja del Home. Sin este
  // parámetro, comportamiento histórico (cartera vigente del día).
  const [searchParams] = useSearchParams()
  const runParam = searchParams.get('run')
  const runType = runParam === 'run1' || runParam === 'run2' ? runParam : undefined
  const { data, isLoading, isError, error, refetch } = usePicks(true, date, runType)
  const [selectedPick, setSelectedPick] = useState<PickLeg | null>(null)
  // Sprint "Product Experience V2" (10/09/2026, mockup aprobado): Top IA/Más Seguros/
  // Secundarios pasan de 3 secciones apiladas a pestañas -- misma primera lectura en menos
  // de 5 segundos que pide el encargo, sin obligar a bajar por las 3 a la vez. MISMOS datos
  // ya cargados por `usePicks`, nunca una consulta nueva por pestaña.
  const [tab, setTab] = useState<PicksTab>('top_ia')

  const topIA = data?.featured ?? []
  const safest = data?.safest_top_picks ?? []
  const secondary = data?.secondary ?? []

  return (
    <div className="p-4 space-y-5">
      <DateNav date={date} onChange={setDate} />
      {runType && <RunBadge runType={runType} />}

      {isLoading && <SkeletonList count={3} />}
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}

      {data && data.total === 0 && (
        <EmptyState
          icon={Star}
          title={
            isToday && !data.exists
              ? 'El análisis de hoy aún no termina'
              : 'No hay picks publicados ese día'
          }
          body={
            isToday && !data.exists
              ? 'El sistema mostrará aquí las recomendaciones cuando finalice el análisis diario.'
              : 'El sistema no encontró recomendaciones que cumplieran los criterios ese día.'
          }
        />
      )}

      {data && data.total > 0 && (
        <>
          <PillTabs
            items={[
              { key: 'top_ia', label: 'Top IA', count: topIA.length },
              { key: 'safest', label: 'Más Seguros', count: safest.length },
              { key: 'secondary', label: 'Secundarios', count: secondary.length },
            ]}
            active={tab}
            onChange={(k) => setTab(k as PicksTab)}
          />

          {tab === 'top_ia' && (
            <section className="space-y-3">
              <SectionHeader
                title="Top 3 IA • Valor"
                description="Recomendaciones seleccionadas por la IA buscando el mejor equilibrio entre probabilidad, cuota y rentabilidad esperada a largo plazo."
              />
              {topIA.length === 0 ? (
                <EmptyState icon={Star} title="Sin Top IA hoy" body="El sistema no encontró recomendaciones que cumplieran los criterios ese día." />
              ) : (
                topIA.map((pick, i) => (
                  <TopPickCard
                    key={pick.decision_record_id}
                    pick={pick}
                    rank={i + 1}
                    variant="ai"
                    onSelect={() => setSelectedPick(pick)}
                  />
                ))
              )}
              {data.performance && (
                <div className="pt-2">
                  <CategoryPerformanceCard title="RENDIMIENTO TOP 3 IA • VALOR" stats={data.performance.ai_top_picks} variant="ai" />
                </div>
              )}
            </section>
          )}

          {tab === 'safest' && (
            <section className="space-y-3">
              <SectionHeader
                title="Top 3 Más Seguros"
                description="Recomendaciones seleccionadas por su mayor probabilidad estimada de acierto dentro de una cuota aceptable."
              />
              {safest.length === 0 ? (
                <EmptyState icon={Star} title="Sin Más Seguros hoy" body="El sistema no encontró recomendaciones que cumplieran los criterios ese día." />
              ) : (
                safest.map((pick, i) => (
                  <TopPickCard
                    key={pick.decision_record_id}
                    pick={pick}
                    rank={i + 1}
                    variant="safest"
                    onSelect={() => setSelectedPick(pick)}
                  />
                ))
              )}
              {data.performance && (
                <div className="pt-2">
                  <CategoryPerformanceCard title="RENDIMIENTO TOP 3 MÁS SEGUROS" stats={data.performance.safest_top_picks} variant="safest" />
                </div>
              )}
            </section>
          )}

          {tab === 'secondary' && (
            <section className="space-y-2">
              <SectionHeader title="Recomendaciones Secundarias IA" />
              {secondary.length === 0 ? (
                <EmptyState icon={Star} title="Sin secundarias hoy" body="El sistema no encontró recomendaciones adicionales que cumplieran los criterios ese día." />
              ) : (
                secondary.map((pick) => (
                  <SecondaryPickCard key={pick.decision_record_id} pick={pick} onSelect={() => setSelectedPick(pick)} />
                ))
              )}
            </section>
          )}
        </>
      )}

      {/* P0.51.1 (29/08/2026, Sección G/K del encargo): trial FREE vencido -- Top Picks
          también queda bloqueado (antes era el único contenido que un FREE conservaba
          siempre). Un solo mensaje claro, en vez del genérico "picks adicionales en PRO"
          (`locked_secondary`), que no explicaba por qué el FREE ya no ve ni su Top 1. */}
      {data?.trial_expired ? (
        <UpgradePrompt requiredPlan="PRO" feature="Top Picks" trialExpired />
      ) : (
        data?.locked_secondary && (
          <UpgradePrompt requiredPlan="PRO" feature="Los otros picks del día" />
        )
      )}

      {data && data.total > 0 && <ResponsibleNotice />}

      {selectedPick && <PickDetailSheet pick={selectedPick} onClose={() => setSelectedPick(null)} />}
    </div>
  )
}
